import {
  useCallback, useState,
} from 'react';
import { GeneralErrorCode } from '~/_shared/types/responseErrors/_shared/generalErrorCodes.types';
import { translateErrorCodesInError } from '~/_shared/types/responseErrors/responseErrorCodes.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type MapUpdateErrorResponse,
  type MapUpdateRequest, updateMapData,
} from './map.repository';
import { MapInfoInputField } from './mapInfoInputField.enum';

type UpdateMapDataArguments = {
  clientId: number;
  mapId: number;
  request: MapUpdateRequest;
};

export const useMapDataUpdate = () => {
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Map<MapInfoInputField, readonly string[]>>(new Map());
  const [globalError, setGlobalError] = useState<string | null>(null);

  const updateMapDataRequest = useCallback(({ clientId, mapId, request }: UpdateMapDataArguments) => {
    setIsLoading(true);

    return updateMapData(clientId, mapId, request)
      .catch((e: MapUpdateErrorResponse) => {
        const fieldErrorsTranslated = translateErrorCodesInError(e.errors || {}, t);

        const knownFieldErrors = new Map(Object.values(MapInfoInputField)
          .filter(field => {
            return fieldErrorsTranslated.hasOwnProperty(field);
          })
          .map(field => [field, fieldErrorsTranslated[field] ?? []] as const));

        const globalError = knownFieldErrors.size ? null : t(GeneralErrorCode.UNKNOWN_ERROR);
        setFieldErrors(knownFieldErrors);
        setGlobalError(globalError);

        throw new Error('Map update failed');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [t]);

  const clearErrors = useCallback(() => {
    setFieldErrors(new Map());
    setGlobalError(null);
  }, []);

  return {
    isLoading,
    updateMapData: updateMapDataRequest,
    clearErrors,
    fieldErrors,
    globalError,
  };
};
